import { ChangeEvent, useState } from "react";
import { Info, INFO_VARIANT } from "components/UI/Info";
import { Loader } from "components/UI/Loader";
import { Modal } from "components/UI/Modal";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useTranslation } from "localization/localization";
import { mergeImportedGroups, selectCustomPallets, selectGroups, selectUnitSystem, setUnitSystem } from "store";
import { exportPackingListXlsx } from "utils/xlsxExport.utils";
import { importPackingListXlsx } from "utils/xlsxImport.utils";
import { CargoGroup } from "./CargoGroup";
import { ControlsBar } from "./ControlsBar";
import s from "./PackingList.module.scss";

type ImportStatus = "idle" | "loading" | "success" | "error";

export const PackingList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const groups = useAppSelector(selectGroups);
  const customPallets = useAppSelector(selectCustomPallets);
  const unitSystem = useAppSelector(selectUnitSystem);
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [fileName, setFileName] = useState("");

  const hasCargo = groups.some((group) => group.cargoRows.length > 0);

  const handleExport = () => {
    exportPackingListXlsx(groups, customPallets, unitSystem);
  };

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>, targetGroupId?: string) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      setStatus("loading");
      setFileName(file.name);

      const { groups: importedGroups, unitSystem } = await importPackingListXlsx(file);
      if (importedGroups.length) dispatch(mergeImportedGroups({ groups: importedGroups, targetGroupId }));
      if (unitSystem) dispatch(setUnitSystem(unitSystem));

      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <>
      <ControlsBar onImport={handleImportFile} onExport={handleExport} exportDisabled={!hasCargo} />

      {status === "success" && (
        <Info variant={INFO_VARIANT.SUCCESS} onClose={() => setStatus("idle")}>
          <strong>{fileName}</strong> {t("file uploaded successfully")}
        </Info>
      )}

      {status === "error" && (
        <Info variant={INFO_VARIANT.ERROR} onClose={() => setStatus("idle")}>
          <strong>{fileName}</strong> {t("file uploaded error")}
        </Info>
      )}

      <div className={s.list}>
        {groups.map((group) => (
          <CargoGroup key={group.id} groupId={group.id} onImport={handleImportFile} />
        ))}
      </div>

      <Modal isOpen={status === "loading"} centered modalClassname={s.loaderModal}>
        <Loader text={t("Loading")} />
      </Modal>
    </>
  );
};
